import EyeFilled from '@ant-design/icons/lib/icons/EyeFilled'
import EyeInvisibleFilled from '@ant-design/icons/lib/icons/EyeInvisibleFilled'
import Button from 'antd/lib/button'
import Flex from 'antd/lib/flex'
import Search from 'antd/lib/input/Search'
import { ClassesPropsType } from 'Components/tasks/Classes/Classes'
import { isEmpty } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { ClassAnnotation, ClassItem } from 'Types/Classes.types'
import classes from './Classes.module.less'
import Icon from '@ant-design/icons/lib/components/Icon'
import { FocusIcon } from 'Components/common/Icons/FocusIcon'
import { UnFocusIcon } from 'Components/common/Icons/UnfocusedIcon'
import Tooltip from 'antd/lib/tooltip'

const VisibleEyeComponent = (props: {
    id: string
    isVisible: boolean
    annotation?: ClassAnnotation
    setClassVisibility: (id: string, isVisible: boolean) => void
}) => {
    if (isEmpty(props.annotation?.selection.individualRowSelectionRange)) {
        return <EyeInvisibleFilled className={classes['empty-icon']} />
    } else {
        return (
            <Button
                onClick={() => props.setClassVisibility(props.id, !props.isVisible)}
                data-testid={`toggle-visibility-${props.id}`}
            >
                {props.isVisible ? (
                    <EyeFilled className={classes['visible-icon']} />
                ) : (
                    <EyeInvisibleFilled className={classes['hidden-icon']} />
                )}
            </Button>
        )
    }
}

const FocusComponent = (props: {
    id: string
    focusedClass: string | null
    onClickFocus: (id: string) => void
}) => {
    return (
        <Button
            onClick={() => props.onClickFocus(props.id)}
            data-testid={`toggle-focus-${props.id}`}
        >
            {props.focusedClass === props.id ? (
                <Icon className={classes['class-focus-selected']} component={FocusIcon} />
            ) : (
                <Icon className={classes['class-focus-default']} component={UnFocusIcon} />
            )}
        </Button>
    )
}

export const ClassesBody = (props: ClassesPropsType) => {
    const [filteredClasses, setFilteredClasses] = useState<ClassItem[]>()
    const [searchText, setSearchText] = useState<string>('')
    const [focusedClass, setFocusedClass] = useState<string | null>(null)

    const isAllClassesHidden = props.classes.every(classItem => !classItem.isVisible)

    useEffect(() => {
        setFilteredClasses(props.classes.filter(classItem => classItem.isVisible))
    }, [props.classes])

    useEffect(() => {
        if (searchText === '') {
            setFilteredClasses(props.classes)
        } else {
            setFilteredClasses(
                props.classes.filter(classItem =>
                    classItem.label.toLowerCase().includes(searchText.toLowerCase())
                )
            )
        }
    }, [props.classes, searchText])

    const onClickFocus = useCallback(
        (id: string) => {
            setFocusedClass(id === focusedClass ? null : id)
            props.setClassFocus(id, id !== focusedClass)
        },
        [focusedClass, props]
    )

    const onClickVisibleIcon = useCallback(
        (id: string, value: boolean) => {
            setFocusedClass(null)
            props.setClassVisibility(id, value)
        },
        [props]
    )

    return (
        <div className={classes['classes-body']}>
            <Flex gap={4} className={classes['class-controls']}>
                <Search
                    placeholder="Search classes..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    className={classes.search}
                />

                <Tooltip title="Show/Hide all annotations" placement="bottomLeft">
                    <Button
                        className={classes['toggle-visibility-button']}
                        onClick={() => onClickVisibleIcon('all', isAllClassesHidden)}
                        data-testid="toggle-visibility"
                    >
                        {isAllClassesHidden ? <EyeInvisibleFilled /> : <EyeFilled />}
                    </Button>
                </Tooltip>
            </Flex>

            {filteredClasses?.map(classItem => (
                <Flex
                    className={
                        classes['class-item'] +
                        ' ' +
                        (props.getActiveClass() === classItem.id ? classes['active'] : '')
                    }
                    justify="space-between"
                    align="center"
                    gap={8}
                    onClick={() => props.setActiveClass(classItem.id)}
                    key={classItem.id}
                >
                    <div
                        className={classes['class-item-color']}
                        style={{ backgroundColor: classItem.color }}
                    ></div>
                    <div className={classes['class-item-name']}>{classItem.label}</div>
                    <div className={classes['class-item-action']}>
                        <FocusComponent
                            id={classItem.id}
                            focusedClass={focusedClass}
                            onClickFocus={onClickFocus}
                        />
                        <VisibleEyeComponent
                            isVisible={classItem.isVisible}
                            annotation={props
                                .getAnnotationList()
                                .find(annotation => annotation.classId === classItem.id)}
                            id={classItem.id}
                            setClassVisibility={onClickVisibleIcon}
                        />
                    </div>
                </Flex>
            ))}
        </div>
    )
}
